import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Target, Plus, ChevronDown, ChevronUp, Trash2, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GoalProgressBar } from './GoalProgressBar';
import { GoalSelectionModal } from './GoalSelectionModal';
import { goalService } from '@/lib/api/goal.service';
import type { GoalInstanceWithDetails, GoalEntityType, GoalStatus } from '@/types/goal.types';

interface GoalsCardProps {
  entityType: GoalEntityType;
  entityId: number;
  canEdit?: boolean;
}

export const GoalsCard: React.FC<GoalsCardProps> = ({
  entityType,
  entityId,
  canEdit = true,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['goals', 'common']);
  const [instances, setInstances] = useState<GoalInstanceWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGoals, setExpandedGoals] = useState<Set<number>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadGoals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await goalService.getInstancesByEntity(entityType, entityId);
      setInstances(data);
    } catch (err) {
      setError(t('goals:card.errors.loadGoals'));
      console.error('Error loading goals:', err);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const toggleExpand = (goalId: number) => {
    setExpandedGoals(prev => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  const handleGoalSelected = async (definitionId: number) => {
    await goalService.createInstance({
      goal_template_id: definitionId,
      entity_type: entityType,
      entity_id: entityId
    });
    await loadGoals();
  };

  const handleDeleteGoal = async (instanceId: number) => {
    if (!window.confirm(t('goals:card.confirmations.removeGoal'))) return;
    
    try {
      setDeletingId(instanceId);
      await goalService.deleteInstance(instanceId, false);
      await loadGoals();
    } catch (err) {
      console.error('Error deleting goal:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status: GoalStatus, totalTasks = 0) => {
    if (status === 'new') {
      return <Badge variant="outline" size="sm">{t('goals:card.statuses.awaitingInit')}</Badge>;
    }
    if (status === 'not_started') {
      return totalTasks > 0
        ? <Badge variant="secondary" size="sm">{t('goals:card.statuses.initialized')}</Badge>
        : <Badge variant="outline" size="sm">{t('goals:card.statuses.awaitingInit')}</Badge>;
    }
    if (status === 'initialized') {
      return <Badge variant="secondary" size="sm">{t('goals:card.statuses.initialized')}</Badge>;
    }
    if (status === 'blocked') {
      return <Badge variant="destructive" size="sm">{t('goals:card.statuses.blocked')}</Badge>;
    }
    if (status === 'completed') {
      return <Badge variant="active" size="sm">{t('goals:card.statuses.completed')}</Badge>;
    }
    return <Badge variant="secondary" size="sm">{t('goals:card.statuses.inProgress')}</Badge>;
  };

  const attachedGoalIds = instances.map(i => i.goal_template_id);
  const completedGoals = instances.filter(i => i.status === 'completed').length;

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">{t('goals:card.sections.title')}</span>
          <Badge variant="secondary" size="sm">
            {instances.length > 0 ? t('goals:card.badges.completedCount', { completed: completedGoals, total: instances.length }) : '0'}
          </Badge>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setShowModal(true)}>
            <Plus className="h-3 w-3 mr-1" />{t('goals:card.actions.add')}
          </Button>
        )}
      </div>

      <div className="divide-y">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <p className="text-sm text-red-500">{error}</p>
            <Button variant="link" size="sm" onClick={loadGoals}>{t('common:actions.retry')}</Button>
          </div>
        ) : instances.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Target className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm font-medium">{t('goals:card.empty.title')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('goals:card.empty.description')}</p>
            {canEdit && (
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowModal(true)}>
                {t('goals:card.actions.addGoal')}
              </Button>
            )}
          </div>
        ) : (
          instances.map((instance) => {
            const isExpanded = expandedGoals.has(instance.id);
            const progress = instance.progress || { total_tasks: 0, completed_tasks: 0, completion_percentage: 0 };
            const isDeleting = deletingId === instance.id;

            return (
              <div key={instance.id} className="group">
                <div
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 cursor-pointer"
                  onClick={() => toggleExpand(instance.id)}
                >
                  <button className="p-0.5 hover:bg-muted rounded">
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span 
                        className="font-medium text-sm truncate hover:text-primary cursor-pointer transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          const goalEntityType = instance.entity_type;
                          const goalEntityId = instance.entity_id;

                          if (goalEntityType === 'celebration') {
                            navigate(`/celebrations/${goalEntityId}/goals/${instance.id}`);
                            return;
                          }

                          if (goalEntityType === 'event') {
                            navigate(`/events/${goalEntityId}/goals/${instance.id}`);
                          }
                        }}
                      >
                        {instance.template?.name || 'Goal'}
                      </span>
                      {getStatusBadge(instance.status, progress.total_tasks)}
                    </div>
                    <div className="mt-1">
                      <GoalProgressBar
                        completedTasks={progress.completed_tasks}
                        totalTasks={progress.total_tasks}
                        showLabel={false}
                      />
                    </div>
                  </div>

                  <span className="text-xs text-muted-foreground">
                    {progress.completed_tasks}/{progress.total_tasks}
                  </span>

                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGoal(instance.id);
                      }}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>

                {isExpanded && (
                  <div className="bg-muted/20 border-t px-4 py-3 ml-8">
                    {instance.template?.description && (
                      <p className="text-xs text-muted-foreground mb-3">
                        {instance.template.description}
                      </p>
                    )}

                    {instance.template?.target_text && (
                      <div className="text-xs mb-3">
                        <span className="font-medium text-green-600">{t('goals:card.labels.target')}</span>{' '}
                        <span className="text-muted-foreground">{instance.template.target_text}</span>
                      </div>
                    )}

                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground mb-2">{t('goals:card.sections.tasks')}</p>
                      {instance.tasks && instance.tasks.length > 0 ? (
                        instance.tasks.map((goalTask) => {
                          const isOverdue = goalTask.task?.due_date && 
                            goalTask.task?.status !== 'completed' && 
                            new Date(goalTask.task.due_date) < new Date();
                          
                          return (
                            <div
                              key={goalTask.id}
                              className="flex items-start gap-2 text-xs py-1"
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                {goalTask.task?.status === 'completed' ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                                ) : (
                                  <Circle className={`h-3.5 w-3.5 ${isOverdue ? 'text-red-500' : 'text-muted-foreground'}`} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={
                                  goalTask.task?.status === 'completed' 
                                    ? 'line-through text-muted-foreground' 
                                    : isOverdue 
                                      ? 'text-red-600 font-medium' 
                                      : ''
                                }>
                                  {goalTask.task?.title || 'Task'}
                                </div>
                                {(goalTask.task?.due_date || goalTask.task?.assigned_to) && (
                                  <div className="flex items-center gap-2 mt-0.5 text-muted-foreground">
                                    {goalTask.task?.due_date && (
                                      <span className={`inline-flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : ''}`}>
                                        <span className="text-[10px]">📅</span>
                                        {new Date(goalTask.task.due_date).toLocaleDateString()}
                                        {isOverdue && <span className="text-[10px]">⚠️</span>}
                                      </span>
                                    )}
                                    {goalTask.task?.assigned_to && (
                                      <span className="inline-flex items-center gap-1">
                                        <span className="text-[10px]">👤</span>
                                        {t('goals:card.labels.owner')}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs text-muted-foreground italic">{t('goals:card.empty.tasks')}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <GoalSelectionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        entityType={entityType}
        onGoalSelected={handleGoalSelected}
        attachedGoalIds={attachedGoalIds}
        manualEntryUrl={entityType === 'celebration' ? `/celebrations/${entityId}/tasks` : `/events/${entityId}/tasks`}
      />
    </div>
  );
};

export default GoalsCard;
